import { Assert } from '@cotera/utilities';
import { Button } from '../../components/ui/button';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useState } from 'react';
import { motion } from 'motion/react';
import { Input } from '../../components/ui/input';
import { ShimmerText } from '../../components/ui/shimmer-text';
import { Loading } from '@cotera/client/app/components';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui/form';
import { Pencil, Trash2 } from 'lucide-react';
import z from 'zod/v3';
import { CreditCardInfoSchema } from '@cotera/api';
import { useClient } from '@cotera/client/app/hooks/use-client';
import { useLogger } from '../../actions/framework/logger';

type CreditCardInfo = z.infer<typeof CreditCardInfoSchema>;

export const CreditCardSettings: React.FC<{
  creditCardInfo: CreditCardInfo;
}> = ({ creditCardInfo }) => {
  const client = useClient();
  const org = useWhoami((s) => s.org);
  const logger = useLogger();

  const returnPath = `/${org.slug}/c/billing-settings`;
  const returnURL = new URL(returnPath, window.location.origin);

  if (creditCardInfo.kind === 'no-saved-card') {
    return (
      <Button
        onClick={async () => {
          const addCCUrl =
            await client.t.billingSettings.generateAddCreditCardSession({
              returnUrl: returnURL.toString(),
            });

          if (addCCUrl.isErr()) {
            logger.error('Error generating add credit card url');
            return;
          }

          window.location.href = addCCUrl.value.addCreditCardUrl;
        }}
      >
        Add A Credit Card
      </Button>
    );
  }

  const { savedCard } = creditCardInfo;

  return (
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-muted-foreground">
          {titleCase(savedCard.kind)} •••• {savedCard.lastFour}
        </p>
        <p className="text-sm text-muted-foreground">
          Expires: {savedCard.expiration}
        </p>
      </div>
      <Button
        variant="outline"
        onClick={async () => {
          const updateCCUrl =
            await client.t.billingSettings.generateUpdateCreditCardSession({
              returnUrl: returnURL.toString(),
            });

          if (updateCCUrl.isErr()) {
            logger.error('Error generating update credit card url');
            return;
          }
          window.location.href = updateCCUrl.value.updateCreditCardUrl;
        }}
      >
        Update Credit Card
      </Button>
    </div>
  );
};

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

type AddCreditsState =
  | {
      t: 'await-click';
    }
  | {
      t: 'await-amount';
    }
  | {
      t: 'await-response';
      amount: number; // max $500
    }
  | {
      t: 'render-response';
      status: 'success' | 'failure';
    };

export const AddCreditsSection: React.FC<{
  className?: string;
  disabled: boolean;
}> = ({ className, disabled }) => {
  const [state, setState] = useState<AddCreditsState>({ t: 'await-click' });
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const queryClient = useQueryClient();

  const handleSubmit = useCallback(async (amount: number) => {
    const response = await client.billingSettings.addCredits({ amount });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.settings.billing.credit_balance(orgId),
    });

    await queryClient.invalidateQueries({
      queryKey: queryKeys.settings.billing.all(orgId),
    });

    const success = response.isOk() && response.value.status === 'success';

    setState({ t: 'render-response', status: success ? 'success' : 'failure' });

    // all dependencies are reference safe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={className}>
      {state.t === 'await-click' ? (
        <Button
          className="px-2 py-1"
          disabled={disabled}
          onClick={() => setState({ t: 'await-amount' })}
        >
          Add Credits
        </Button>
      ) : state.t === 'await-amount' ? (
        <AddCreditsAmountInput
          onSubmit={async (amount) => {
            setState({ t: 'await-response', amount });
            await handleSubmit(amount);
          }}
        />
      ) : state.t === 'await-response' ? (
        <ShimmerText variant="muted" className="text-sm">
          Adding credits…
        </ShimmerText>
      ) : state.t === 'render-response' ? (
        <p className="text-sm text-muted-foreground">
          {state.status === 'success'
            ? 'Credits Successfully Added. (Balance will update shortly.)'
            : 'Failed to add credits. (Check that your payment method is valid. If this problem persists, contact support.)'}
        </p>
      ) : (
        Assert.unreachable(state)
      )}
    </div>
  );
};

const AddCreditsAmountInput: React.FC<{
  onSubmit: (amount: number) => void;
}> = ({ onSubmit }) => {
  const [amount, setAmount] = useState<number | ''>(0);
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm">$</span>
      <motion.div
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: 128, opacity: 1 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="overflow-hidden"
      >
        <Input
          className="w-32 min-w-32"
          value={amount}
          type="number"
          min={0}
          max={500}
          onChange={(e) => {
            const value = e.target.value;
            setAmount(value === '' ? '' : Number(e.target.value));
          }}
        />
      </motion.div>
      <Button
        className="px-2 py-1"
        onClick={() => onSubmit(amount === '' ? 0 : amount)}
        disabled={amount === ''}
      >
        Add Credits
      </Button>
    </div>
  );
};

type AutoTopUpState =
  | {
      t: 'render-existing';
      existingConfig: TopUpConfig;
      submitStatus?: 'success' | 'failure'; // if a response was submitted, render new existing & status
    }
  | {
      t: 'edit-existing';
      existingConfig: TopUpConfig;
    }
  | {
      t: 'submitting';
      // newConfig: TopUpConfig;
    };

export const AutoTopUpSection: React.FC<{
  className?: string;
  currentTopUp: TopUpConfig;
}> = ({ className, currentTopUp }) => {
  const { mutateTopUp } = useMutateTopUps();
  const [state, setState] = useState<AutoTopUpState>({
    t: 'render-existing',
    existingConfig: currentTopUp,
  });

  const handleSubmit = useCallback(
    async (config: TopUpConfig): Promise<void> => {
      setState({ t: 'submitting' });
      await mutateTopUp(config);
      setState({
        t: 'render-existing',
        existingConfig: config,
        submitStatus: 'success',
      });
    },

    // all dependencies are reference safe
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <div className={className}>
      <div className="text-sm font-medium mb-2">Automatic Top Ups</div>
      {state.t === 'render-existing' ? (
        <div className="flex items-center justify-between">
          <RenderTopUpConfig config={state.existingConfig} />
          <div className="flex gap-2">
            <Button
              size="icon-md"
              variant={'outline'}
              onClick={() =>
                setState({
                  t: 'edit-existing',
                  existingConfig: state.existingConfig,
                })
              }
            >
              <Pencil className="h-4 w-4" />
            </Button>
            {state.existingConfig && (
              <Button
                size="icon-md"
                variant={'outline'}
                onClick={async () => {
                  await handleSubmit(null);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ) : state.t === 'edit-existing' ? (
        <TopUpForm
          existingConfig={state.existingConfig}
          onSubmit={handleSubmit}
          onCancel={() =>
            setState({
              t: 'render-existing',
              existingConfig: state.existingConfig,
            })
          }
        />
      ) : state.t === 'submitting' ? (
        <Loading.Spinner />
      ) : (
        Assert.unreachable(state)
      )}
    </div>
  );
};

const RenderTopUpConfig: React.FC<{ config: TopUpConfig }> = ({ config }) => {
  return (
    <p className="text-sm text-muted-foreground">
      {config === null
        ? 'No automatic top-up configured.'
        : `$${config.amount} will be added to credit balance when balance dips below threshold of $${config.threshold}`}
    </p>
  );
};

/** expects onSubmit to be reference safe  */
const TopUpForm: React.FC<{
  existingConfig: TopUpConfig;
  onSubmit: (config: TopUpConfig) => Promise<void>;
  onCancel: () => void;
}> = ({ existingConfig, onSubmit, onCancel }) => {
  const form = useForm<{
    amount: number | '';
    threshold: number | '';
  }>({
    defaultValues: existingConfig ?? undefined,
  });

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((data) =>
          onSubmit({
            amount: data.amount === '' ? 0 : data.amount,
            threshold: data.threshold === '' ? 0 : data.threshold,
          })
        )}
      >
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount</FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  $
                  <Input
                    className="w-32"
                    min={0}
                    max={500}
                    type="number"
                    required
                    {...field}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="threshold"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Threshold</FormLabel>
              <FormControl>
                <div className="flex items-center gap-2">
                  $
                  <Input
                    className="w-32"
                    min={0}
                    max={500}
                    type="number"
                    required
                    {...field}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2">
          <Button className="px-2 py-1" type="submit">
            Save
          </Button>
          <Button className="px-2 py-1" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
};

/**
 * If balance falls below threshold, add amount to balance.
 *
 * Amount should be bigger than threshold
 */
type TopUpConfig = {
  amount: number;
  threshold: number;
} | null;

/**
 * Gives the user's current top up configuration,
 *
 * as well as a function to mutate the user's orb-side top up configuration.
 *
 * Note that the mutation function is reference safe (can live independently within a useCallback)
 */
const useMutateTopUps = (): {
  mutateTopUp: (newTopUp: TopUpConfig) => void;
} => {
  const client = useTenantedClient();
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);

  const mutateTopUp = useCallback(async (newTopUp: TopUpConfig) => {
    const res = await client.billingSettings.setTopUpConfig({
      config: newTopUp,
    });

    Assert.assert(res.isOk() && res.value.status === 'success');

    await queryClient.invalidateQueries({
      queryKey: queryKeys.settings.billing.all(orgId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    mutateTopUp,
  };
};
